import React from 'react';

/**
 * MobileListCard - Composant de carte réutilisable pour les listes mobiles
 * Utilisé pour: Qualifications, Relevé d'heures, Validation d'identité, etc.
 * 
 * @param {Object} props
 * @param {string} props.title - Titre principal de la carte
 * @param {string} props.subtitle - Sous-titre optionnel
 * @param {React.ReactNode} props.badge - Badge ou chip à afficher sous le titre
 * @param {React.ReactNode} props.topRightBadge - Badge en haut à droite (ex: secteur)
 * @param {React.ReactNode} props.statusBadge - Badge de statut sur la ligne de date (ex: désactivé, validé)
 * @param {string} props.date - Date à afficher
 * @param {string} props.dateLabel - Label pour la date (ex: "Créé le", "Période")
 * @param {Array} props.metaItems - Tableau d'items de métadonnées [{icon, label, value}]
 * @param {React.ReactNode} props.action - Bouton ou icône d'action
 * @param {Function} props.onClick - Callback au clic sur la carte
 * @param {boolean} props.isSelected - Si la carte est sélectionnée
 * @param {React.ReactNode} props.warning - Icône ou message d'avertissement
 * @param {React.ReactNode} props.children - Contenu personnalisé supplémentaire
 * @param {string} props.className - Classes CSS additionnelles
 */
const MobileListCard = ({
  title,
  subtitle,
  badge,
  topRightBadge,
  statusBadge,
  date,
  dateLabel,
  metaItems = [],
  action,
  onClick,
  isSelected = false,
  warning,
  children,
  className = '',
}) => {
  return (
    <div
      className={`
        bg-white rounded-xl border transition-all duration-200 overflow-hidden
        ${isSelected 
          ? 'border-yellow-500 shadow-md ring-1 ring-yellow-200' 
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }
        ${onClick ? 'cursor-pointer active:scale-[0.99]' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {/* Header avec titre et badge/action en haut à droite */}
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Titre avec warning */}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-base truncate">
                {title}
              </h3>
              {warning && <div className="flex-shrink-0">{warning}</div>}
            </div>
            
            {/* Sous-titre */}
            {subtitle && (
              <p className="text-sm text-gray-500 mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
          
          {/* Badge en haut à droite (secteur, type) */}
          {topRightBadge && (
            <div className="flex-shrink-0">
              {topRightBadge}
            </div>
          )}
          
          {/* Bouton d'action (si pas de topRightBadge) */}
          {action && !topRightBadge && (
            <div 
              className="flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {action}
            </div>
          )}
        </div>
        
        {/* Badge sous le titre */}
        {badge && (
          <div className="mt-2.5">
            {badge}
          </div>
        )}
      </div>
      
      {/* Métadonnées + Date + StatusBadge */}
      {(metaItems.length > 0 || date || statusBadge) && (
        <div className="px-4 pb-4">
          {/* Si on a des metaItems, les afficher avec wrap sur 2 lignes */}
          {metaItems.length > 0 && (
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500">
              {metaItems.map((item, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                  {item.label && <span className="text-gray-400 whitespace-nowrap">{item.label}</span>}
                  <span className="text-gray-600">{item.value}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Si on a une date sans metaItems, l'afficher normalement */}
          {date && metaItems.length === 0 && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5 text-sm text-gray-500">
                {dateLabel && <span className="text-gray-400">{dateLabel}</span>}
                <span className="text-gray-600">{date}</span>
              </div>
              
              {/* Status badge à droite de la date */}
              {statusBadge && (
                <div className="flex-shrink-0">
                  {statusBadge}
                </div>
              )}
            </div>
          )}
          
          {/* Status badge seul si pas de date ni metaItems */}
          {statusBadge && !date && metaItems.length === 0 && (
            <div className="flex justify-end">
              {statusBadge}
            </div>
          )}
        </div>
      )}
      
      {/* Contenu personnalisé */}
      {children && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default MobileListCard;

